import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { toast } from 'react-toastify';

// API base URL - dynamically construct for production
const getApiBaseUrl = () => {
  // If we have an explicit API URL set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // In production (Vercel), use the current domain
  if (process.env.NODE_ENV === 'production' && typeof window !== 'undefined') {
    return `${window.location.origin}/api`;
  }
  
  // Default to localhost for development
  return 'http://localhost:5001/api';
};

const API_BASE_URL = getApiBaseUrl();

// Auth Context
const AuthContext = createContext();

// Auth Actions
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  UPDATE_USER: 'UPDATE_USER',
  SET_LOADING: 'SET_LOADING'
};

// Initial State
const initialState = {
  user: null,
  token: localStorage.getItem('authToken'),
  isAuthenticated: !!localStorage.getItem('authToken'),
  isLoading: true,
  error: null
};

// Auth Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        isLoading: true,
        error: null
      };
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
    
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload
      };
    
    case AUTH_ACTIONS.LOGOUT:
      localStorage.removeItem('authToken');
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      };
    
    case AUTH_ACTIONS.UPDATE_USER:
      return {
        ...state,
        user: { ...state.user, ...action.payload }
      };
    
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
    
    default:
      return state;
  }
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // API Helper Function
  const apiCall = async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers = {
      ...options.headers
    };

    // Don't set Content-Type for FormData - let browser set it with boundary
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Add Authorization header if token exists
    if (state.token) {
      headers.Authorization = `Bearer ${state.token}`;
    }

    const config = {
      headers,
      ...options
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        // Create error object that preserves backend error structure
        const error = new Error(data.message || 'An error occurred');
        error.errors = data.errors; // Preserve validation errors array
        throw error;
      }

      return data;
    } catch (error) {
      console.error('API call error:', error);
      throw error;
    }
  };

  // Login Function
  const login = async (email, password) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });

    try {
      const data = await apiCall('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      });

      // Store JWT token in localStorage
      localStorage.setItem('authToken', data.token);
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: {
          user: data.user,
          token: data.token
        }
      });

      toast.success(`Welcome back, ${data.user.firstName}!`);
      return data;
    } catch (error) {
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: error.message
      });
      toast.error(error.message);
      throw error;
    }
  };

  // Logout Function
  const logout = async () => {
    try {
      // Call logout endpoint (optional with JWT)
      await apiCall('/auth/logout', { method: 'POST' });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Remove token from localStorage
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      toast.info('You have been logged out');
    }
  };

  // Get Current User
  const getCurrentUser = async () => {
    try {
      const data = await apiCall('/auth/me');
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: {
          user: data.user,
          token: state.token // Keep existing token
        }
      });
    } catch (error) {
      console.error('Get current user error:', error);
      // Session might be invalid, logout
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Update User Profile
  const updateProfile = async (userData) => {
    try {
      const data = await apiCall('/auth/profile', {
        method: 'PUT',
        body: JSON.stringify(userData)
      });

      dispatch({
        type: AUTH_ACTIONS.UPDATE_USER,
        payload: data.user
      });

      toast.success('Profile updated successfully');
      return data.user;
    } catch (error) {
      toast.error(error.message);
      throw error;
    }
  };

  // Change Password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      await apiCall('/auth/change-password', {
        method: 'PUT',
        body: JSON.stringify({ currentPassword, newPassword })
      });

      toast.success('Password changed successfully');
    } catch (error) {
      toast.error(error.message);
      throw error;
    }
  };

  // Check if user has required role
  const hasRole = (requiredRole) => {
    if (!state.user) return false;
    
    const roleHierarchy = {
      'system_admin': 3,
      'admin': 2,
      'coach': 1
    };
    
    const userRoleLevel = roleHierarchy[state.user.role] || 0;
    const requiredRoleLevel = roleHierarchy[requiredRole] || 0;
    
    return userRoleLevel >= requiredRoleLevel;
  };

  // Check if user is admin or higher
  const isAdmin = () => hasRole('admin');

  // Check if user is system admin
  const isSystemAdmin = () => hasRole('system_admin');

  // Refresh Token
  const refreshToken = async () => {
    try {
      const data = await apiCall('/auth/refresh', { method: 'POST' });
      
      // Update token in localStorage and state
      localStorage.setItem('authToken', data.token);
      
      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: {
          user: state.user,
          token: data.token
        }
      });
      
      toast.success('Token refreshed successfully');
      
      return data.token;
    } catch (error) {
      console.error('Token refresh error:', error);
      logout();
      throw error;
    }
  };

  // Initialize auth state on mount
  useEffect(() => {
    // Only try to get current user if we have a token
    if (state.token) {
      getCurrentUser();
    } else {
      // No token, just set loading to false
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  }, []);

  // Context value
  const value = {
    // State
    user: state.user,
    token: state.token,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    
    // Actions
    login,
    logout,
    updateProfile,
    changePassword,
    refreshToken,
    
    // Utilities
    hasRole,
    isAdmin,
    isSystemAdmin,
    apiCall
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

// HOC for components that need authentication
export const withAuth = (Component) => {
  return function AuthenticatedComponent(props) {
    const auth = useAuth();
    
    if (auth.isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="spinner spinner-primary w-8 h-8"></div>
        </div>
      );
    }
    
    if (!auth.isAuthenticated) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
            <p className="text-gray-600 mb-4">You must be logged in to access this page.</p>
            <button
              onClick={() => window.location.href = '/login'}
              className="btn-primary"
            >
              Go to Login
            </button>
          </div>
        </div>
      );
    }
    
    return <Component {...props} />;
  };
};

export default AuthContext;